/**
 * Profile hub. Ported from the original app's ProfileMenu.tsx — same UI verbatim,
 * with next/link → react-router Link, useRouter → useNavigate, and useAuth for the
 * user + logout. The subscription/billing link is dropped (single-coach, no tiers).
 */

import { Link, useNavigate } from 'react-router';
import {
  Heart,
  Ruler,
  MessageCircle,
  Bot,
  BookOpen,
  Coins,
  Quote,
  Settings,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';

const menuItems = [
  { href: '/profile/health', label: 'Health Data', icon: Heart },
  { href: '/profile/measurements', label: 'Body Measurements', icon: Ruler },
  { href: '/profile/credits', label: 'Wallet', icon: Coins },
  { href: '/profile/messages', label: 'Messages', icon: MessageCircle },
  { href: '/profile/ai-coach', label: 'AI Coach', icon: Bot },
  { href: '/community', label: 'Journal', icon: BookOpen },
  { href: '/profile/testimonial', label: 'Leave a Testimonial', icon: Quote },
  { href: '/profile/settings', label: 'Settings', icon: Settings },
];

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const email = user?.email ?? '';
  const avatarUrl = user?.avatar_url ?? null;

  // display_name -> "First Last" -> email local-part. Never blank / "null null".
  const name =
    user?.display_name?.trim() ||
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
    (email.includes('@') ? email.split('@')[0] : email);
  const avatarInitials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase() || '?';

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <>
      <PageHeader title="More" />
      <div className="px-5 flex flex-col gap-4">
      {/* User Card */}
      <div className="card flex items-center gap-4">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold overflow-hidden"
          style={{ background: 'var(--gold)', color: 'var(--navy)' }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            avatarInitials
          )}
        </div>
        <div>
          <p className="text-white font-semibold text-lg">
            {name}
          </p>
          <p className="text-sm" style={{ color: 'var(--gray-400)' }}>
            {email}
          </p>
        </div>
      </div>

      {/* Menu Items */}
      <div className="card p-0 overflow-hidden">
        {menuItems.map((item, i) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-white/5"
              style={{
                borderTop: i > 0 ? '1px solid rgba(255,255,255,0.06)' : undefined,
              }}
            >
              <Icon size={18} style={{ color: 'var(--gold-muted)' }} />
              <span className="text-white text-sm font-medium flex-1">
                {item.label}
              </span>
              <span style={{ color: 'var(--gray-500)' }}>&rsaquo;</span>
            </Link>
          );
        })}
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="card flex items-center justify-center gap-2 py-3.5 cursor-pointer transition-colors hover:bg-white/5"
        style={{ color: 'var(--error)' }}
      >
        <LogOut size={16} />
        Sign Out
      </button>
      </div>
    </>
  );
}
